/**
 * FilterModal — mobile variant of the global filter bar (IA §9, milestone
 * v1.4 / Plan 04-10). At ≤767px viewport FilterBar renders a single
 * `Filters (N active)` button which toggles this modal; the modal presents
 * every FilterBar control in a vertical stack with a Done / Reset footer.
 *
 * Contract (post Bug F1):
 *   - Every control live-applies via `useUrlFilters().setPending`, which
 *     writes the merged filter straight to the URL. The modal's "Done"
 *     button just closes the sheet — no Apply step.
 *   - Mobile (the intended viewport): full-screen sheet (`h-[100dvh]
 *     w-screen`, no rounded corners). Tablet/desktop fallback: centered card
 *     at `max-w-md`. The modal is ONLY mounted when `isMobile` at FilterBar
 *     level, but the CSS is forgiving if someone opens it on a wider screen.
 *   - Touch targets: every control uses `min-h-11` (44px) per IA §9.
 *   - Radix `Dialog` (already a project dependency) provides focus-trap,
 *     escape key, overlay click, and ARIA hooks — no custom modal primitives.
 *
 * Active filter count rule (from plan must-haves): defaults yield 0; role,
 * cohort, channel, surface, requestType, inputMode, users[], and a custom
 * date range each contribute +1. The
 * '24h' preset still counts as 0 only when it is the default — here the
 * default IS '30d' (see DEFAULT_FILTERS in filters-schema.ts), so '24h' counts
 * as a change. The shared helper `countActiveFilters` lives here and is
 * re-exported to FilterBar so both render the same number.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FilterAxis, InapplicableAxes } from '@/lib/filter-applicability';
import { DEFAULT_FILTERS, type Filters } from '@/lib/filters-schema';

import { DateRangeControl } from './DateRangeControl';
import { UserMultiSelect } from './UserMultiSelect';

type Granularity = Filters['granularity'];

const ROLE_OPTIONS: { value: Filters['role']; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'rm', label: 'RM' },
  { value: 'non-rm', label: 'Non-RM' },
];

// BUG E-Cohort — absent field = no segmentation. See FilterBar.tsx for why
// the UI keeps a sentinel 'all' option.
type CohortUiValue = 'all' | 'active' | 'engaged' | 'power';
const COHORT_OPTIONS: { value: CohortUiValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'power', label: 'Power' },
];
const cohortFilterToUi = (v: Filters['cohort']): CohortUiValue => v ?? 'all';
const cohortUiToFilter = (v: string): Filters['cohort'] =>
  v === 'all' || v === '' ? undefined : (v as Exclude<CohortUiValue, 'all'>);

// BUG G2 — `group_dm` removed (mirror of FilterBar.tsx). Coach isn't used
// in group DMs. Legacy URLs coerce back to `any` via filters-schema.ts.
const CHANNEL_OPTIONS: { value: Filters['channel']; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'dm', label: 'DM' },
  { value: 'channel', label: 'Channel' },
];

// DayFilter — mobile mirror of the FilterBar segmented control. Three
// options (All / Weekdays / Weekends); default 'weekdays'. Mobile modal
// uses a Select (same control family as Role/Cohort/Channel here) to
// preserve touch-target rhythm.
const DAY_FILTER_OPTIONS: { value: Filters['dayFilter']; label: string }[] = [
  { value: 'all', label: 'All days' },
  { value: 'weekdays', label: 'Weekdays only' },
  { value: 'weekends', label: 'Weekends only' },
];

// The three 2026-08-19 filters. Both surfaces render the same options, so
// they live in one place — here rather than in FilterBar, because FilterBar
// already imports from this file (countActiveFilters) and the reverse
// direction would be an import cycle.
// Which surface the turn arrived on. Labelled "Channel" for the reader; the
// field is `surface` because `channel` is already the Slack context type.
export const SURFACE_OPTIONS: ReadonlyArray<{ value: Filters['surface']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'slack', label: 'Slack' },
  { value: 'web', label: 'Web' },
];

export const REQUEST_TYPE_OPTIONS: ReadonlyArray<{ value: Filters['requestType']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'coach', label: 'Mr. Edge' },
  { value: 'inbox', label: 'Inbox' },
];

export const INPUT_MODE_OPTIONS: ReadonlyArray<{ value: Filters['inputMode']; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'text', label: 'Text' },
  { value: 'voice', label: 'Voice' },
];

// Admins (2026-08-19). TWO states, not three: there is no "admins only" mode,
// the `users[]` chip already isolates one person by name. Labelled after what
// is true of the DATA — the same grammar as every other control in the bar —
// rather than after what the control does to it.
export const ADMINS_OPTIONS: ReadonlyArray<{ value: Filters['admins']; label: string }> = [
  { value: 'excluded', label: 'Excluded' },
  { value: 'included', label: 'Included' },
];

// Granularity — mobile mirror of the FilterBar segmented control.
// Engagement P1-01 / Overview P2-5: mobile users had no way to change
// time-series bucket size because the desktop FilterBar hides its
// Granularity picker at <md. Mobile modal picks up the same four options
// and writes through to the same `granularity` URL param.
const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

/**
 * Count non-default filter selections for the `Filters (N active)` badge.
 *
 * Scope: role ≠ 'all', cohort ≠ 'all', channel ≠ 'any', surface /
 * requestType / inputMode ≠ 'all', admins ≠ 'excluded', users.length > 0,
 * and the date range only counts when it deviates from the default preset
 * ('30d'). A custom range with both `from` and `to` set counts as 1 regardless
 * of dates. Sort state is NOT surfaced to the user in the badge — it isn't a
 * filter in the IA sense, it's a view state.
 *
 * `admins: 'included'` counts even though it REMOVES a predicate rather than
 * adding one. The badge answers "is what I am looking at the default view?",
 * not "how many WHERE clauses are there" — and on mobile, where the controls
 * live behind a button, admitting the admins moves every number on the page
 * with nothing on screen to say so. Granularity stays out: it changes how the
 * same rows are bucketed, not which rows they are. `dayFilter` is out too, but
 * NOT for that reason — `weekendExclusionSQL` removes rows, so counting it
 * would be defensible; it has never been counted and this control did not
 * revisit that.
 */
export function countActiveFilters(f: Filters): number {
  let n = 0;
  const preset = f.preset ?? DEFAULT_FILTERS.preset;
  if (preset !== DEFAULT_FILTERS.preset) n += 1;
  if (f.role !== DEFAULT_FILTERS.role) n += 1;
  if (f.cohort !== DEFAULT_FILTERS.cohort) n += 1;
  if (f.channel !== DEFAULT_FILTERS.channel) n += 1;
  if (f.surface !== DEFAULT_FILTERS.surface) n += 1;
  if (f.requestType !== DEFAULT_FILTERS.requestType) n += 1;
  if (f.inputMode !== DEFAULT_FILTERS.inputMode) n += 1;
  if (f.admins !== DEFAULT_FILTERS.admins) n += 1;
  if ((f.users?.length ?? 0) > 0) n += 1;
  return n;
}

export interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  pendingFilters: Filters;
  setPending: (patch: Partial<Filters>) => void;
  onReset: () => void;
  activeCount: number;
  /**
   * Axes that cannot describe the current tab, each with its reason — the same
   * declaration the desktop bar consumes (lib/filter-applicability.ts), handed
   * down by FilterBar. Until this prop existed the sheet had NO per-route
   * signal at all: it kept offering Type on /inbox and Granularity on
   * /feedback long after the desktop bar had stopped.
   */
  inapplicable?: InapplicableAxes;
}

export function FilterModal({
  open,
  onClose,
  pendingFilters,
  setPending,
  onReset,
  inapplicable = {},
}: FilterModalProps) {
  /**
   * The sheet has no hover, so the desktop `title` affordance is useless here
   * — the reason goes inline as visible text, which is already how this file
   * carries the Type / Input / Admins caveats. Returns the props a row needs:
   * a disabled trigger and a note under it.
   */
  const rowState = (axis: FilterAxis) => {
    const reason = inapplicable[axis];
    return {
      disabled: Boolean(reason),
      note: reason,
      noteId: reason ? `filter-modal-${axis}-inapplicable` : undefined,
    };
  };
  // Radix' onOpenChange fires with `false` on escape / overlay / close button
  // and with `true` when a trigger opens it. This modal is controlled by the
  // parent (FilterBar), so only the close path is wired.
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  const handleReset = () => {
    // Reset clears the URL back to defaults and closes the modal so the
    // user isn't left staring at a flash of defaults before the sheet
    // animates away.
    onReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="filter-modal"
        // Mobile: full-screen sheet. The shadcn DialogContent primitive carries
        // a base `grid gap-4 p-6` that lands later in the compiled CSS than our
        // `flex` utility. Using `!flex` (Tailwind important) guarantees we beat
        // `.grid` in source order so the header / scroll body / sticky footer
        // actually stack as a 3-row flex column instead of collapsing into the
        // same grid cell (that was the root cause of the footer overlapping
        // the Users + Channel controls on iPhone SE).
        //
        // Tablet+: Radix' default centered card layout is restored via `sm:`.
        className="!flex h-[100dvh] w-screen max-w-none flex-col gap-0 p-0 sm:h-auto sm:max-w-md sm:rounded-lg"
      >
        <DialogHeader className="shrink-0 border-b border-theme-border px-4 py-3">
          <DialogTitle className="text-left text-lg font-semibold">
            Filters
          </DialogTitle>
          {/*
            A11y: explicit description so Radix' aria-describedby points at
            meaningful copy. The shared DialogContent wrapper also auto-
            emits a fallback when none is provided — we pass a specific
            one here so screen readers get the modal's actual purpose.
          */}
          <DialogDescription className="sr-only">
            Filter dashboard data by date, role, cohort, users, Slack
            conversation, channel, request type, input mode, whether admin
            Slack accounts are counted, day of week, or time-series
            granularity.
          </DialogDescription>
        </DialogHeader>

        {/*
          Scrollable body. `min-h-0` is required on a flex child that should
          shrink below its content height — without it the child refuses to
          shrink, which is why overflow-y-auto was silently not kicking in
          before (the body grew past the viewport instead of scrolling).
          `gap-5` (20px) matches iOS HIG rhythm between grouped controls;
          the previous `gap-3` (12px) felt cramped on 375-wide screens.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
          <div className="flex flex-col gap-5">
            <DateRangeControl
              inapplicableReason={inapplicable.window}
              stacked
              value={{
                preset: pendingFilters.preset,
                from: pendingFilters.from,
                to: pendingFilters.to,
              }}
              onChange={(next) =>
                setPending({
                  preset: next.preset,
                  from: next.from,
                  to: next.to,
                })
              }
            />

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-role"
                className="text-sm font-medium"
              >
                Role
              </Label>
              <Select
                value={pendingFilters.role}
                onValueChange={(v) =>
                  setPending({ role: v as Filters['role'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-role"
                  className="min-h-11 w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-cohort"
                className="text-sm font-medium"
              >
                Cohort
              </Label>
              <Select
                value={cohortFilterToUi(pendingFilters.cohort)}
                onValueChange={(v) =>
                  setPending({ cohort: cohortUiToFilter(v) })
                }
              >
                <SelectTrigger
                  id="filter-modal-cohort"
                  className="min-h-11 w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COHORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Users</Label>
              <UserMultiSelect
                stacked
                admins={pendingFilters.admins}
                value={pendingFilters.users}
                onChange={(users) => setPending({ users })}
              />
            </div>

            <div className="flex flex-col gap-2">
              {/* Labelled after the values it offers (Any / DM / Channel):
                  this row filters the Slack conversation a turn happened in
                  (`contextType` — see langfuse/sync.ts), not the surface the
                  turn arrived on. It used to read "Channel", which collided
                  with the surface row below once that was added; the surface
                  row keeps the plain name a dashboard reader expects. Web
                  turns have no Slack conversation at all, so narrowing this
                  implies Slack. */}
              <Label
                htmlFor="filter-modal-channel"
                className="text-sm font-medium"
              >
                Slack conversation
              </Label>
              <Select
                value={pendingFilters.channel}
                onValueChange={(v) =>
                  setPending({ channel: v as Filters['channel'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-channel"
                  className="min-h-11 w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* The three 2026-08-19 filters. Placed here — after the
                Slack context-type row, before Days/Granularity — because
                Channel(surface) answers the same question as the row above
                it ("where did the turn arrive"), and Type / Input describe
                the turn itself, while Days and Granularity are terminal
                view options that read best last. Desktop shows these as
                segmented controls; mobile keeps the Select primitive so the
                touch-target rhythm of the sheet is unbroken.
 */}
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-surface"
                className="text-sm font-medium"
              >
                Channel
              </Label>
              <Select
                value={pendingFilters.surface}
                onValueChange={(v) =>
                  setPending({ surface: v as Filters['surface'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-surface"
                  className="min-h-11 w-full"
                  disabled={rowState('surface').disabled}
                  aria-describedby={rowState('surface').noteId}
                  data-testid="filter-modal-surface"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SURFACE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rowState('surface').note && (
                <p
                  id={rowState('surface').noteId}
                  className="text-xs text-theme-text-secondary"
                  data-testid="filter-modal-surface-inapplicable"
                >
                  {rowState('surface').note}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-request-type"
                className="text-sm font-medium"
              >
                Type
              </Label>
              <Select
                value={pendingFilters.requestType}
                onValueChange={(v) =>
                  setPending({ requestType: v as Filters['requestType'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-request-type"
                  className="min-h-11 w-full"
                  disabled={rowState('requestType').disabled}
                  aria-describedby={rowState('requestType').noteId}
                  data-testid="filter-modal-request-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rowState('requestType').note && (
                <p
                  id={rowState('requestType').noteId}
                  className="text-xs text-theme-text-secondary"
                  data-testid="filter-modal-request-type-inapplicable"
                >
                  {rowState('requestType').note}
                </p>
              )}
              {/* Same limitation the desktop tooltip states (R5): capture
                  turns are only identified from 18 Aug 2026. */}
              <p
                className="text-xs text-theme-text-secondary"
                data-testid="filter-modal-request-type-note"
              >
                Inbox means the turn recorded at least one task. A turn is
                recognised as a capture turn only from 18 Aug 2026 — capture
                started on 11 Aug without that marking, so a capture turn
                from that week counts as Mr. Edge.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-input-mode"
                className="text-sm font-medium"
              >
                Input
              </Label>
              <Select
                value={pendingFilters.inputMode}
                onValueChange={(v) =>
                  setPending({ inputMode: v as Filters['inputMode'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-input-mode"
                  className="min-h-11 w-full"
                  disabled={rowState('inputMode').disabled}
                  aria-describedby={rowState('inputMode').noteId}
                  data-testid="filter-modal-input-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INPUT_MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rowState('inputMode').note && (
                <p
                  id={rowState('inputMode').noteId}
                  className="text-xs text-theme-text-secondary"
                  data-testid="filter-modal-input-mode-inapplicable"
                >
                  {rowState('inputMode').note}
                </p>
              )}
              {/* The desktop bar carries this in a tooltip; the sheet has no
                  tooltip affordance, so the caveat is inline. Counting an
                  unmarked turn as text is an assumption, not a measurement —
                  saying so is the point, not decoration. */}
              <p
                className="text-xs text-theme-text-secondary"
                data-testid="filter-modal-input-mode-note"
              >
                A turn counts as Voice only when it was recorded as dictated.
                Where the input mode was not recorded — every web turn, and
                everything before 6 Jul 2026 — it counts as Text. This
                overstates Text.
              </p>
            </div>

            {/* Admins (mobile mirror of the desktop segmented control).
                Placed straight after Type / Input because it answers the same
                kind of question they do — whose turns are on screen — and
                before Days / Granularity, which are terminal view options.
                The desktop bar carries the caveat below in a tooltip; the
                sheet has no tooltip affordance, so it is inline. */}
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-admins"
                className="text-sm font-medium"
              >
                Admins
              </Label>
              <Select
                value={pendingFilters.admins}
                onValueChange={(v) =>
                  setPending({ admins: v as Filters['admins'] })
                }
              >
                <SelectTrigger
                  id="filter-modal-admins"
                  className="min-h-11 w-full"
                  data-testid="filter-modal-admins"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADMINS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className="text-xs text-theme-text-secondary"
                data-testid="filter-modal-admins-note"
              >
                This applies to every tab. Excluded is the default and is how
                every number was counted before this control existed. Only
                turns carrying an admin&apos;s Slack id are affected — a web
                turn carries none, so an admin&apos;s web turns are counted
                either way.
              </p>
            </div>

            {/* DayFilter (mobile). Replaces the old "Show weekends"
                checkbox with a 3-option select — 'All days' / 'Weekdays
                only' / 'Weekends only'. Default is 'weekdays' (mirrors
                DEFAULT_FILTERS.dayFilter and the product decision "по
                умолчанию исключить"). Mobile keeps the Select primitive so
                every filter row in the modal reads the same; the desktop
                FilterBar uses SegmentedControl because the stronger click
                target density fits. The backend safely ignores dayFilter
                at non-daily granularities (weekendExclusionSQL returns
                undefined so WHERE keeps the day-of-week predicate out).
                Positioned last so it reads as a terminal option rather
                than interrupting the Role→Cohort→Users→Channel group. */}
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-day-filter"
                className="text-sm font-medium"
              >
                Days
              </Label>
              <Select
                disabled={rowState("dayFilter").disabled}
                value={pendingFilters.dayFilter}
                onValueChange={(v) =>
                  setPending({ dayFilter: v as Filters['dayFilter'] })
                }
              >
                <SelectTrigger
                  disabled={rowState("dayFilter").disabled}
                  aria-describedby={rowState("dayFilter").noteId}
                  id="filter-modal-day-filter"
                  className="min-h-11 w-full"
                  data-testid="filter-modal-day-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAY_FILTER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rowState('dayFilter').note && (
                <p id={rowState('dayFilter').noteId} data-testid="filter-modal-day-filter-inapplicable" className="text-xs text-muted-foreground">
                  {rowState('dayFilter').note}
                </p>
              )}
            </div>

            {/* Granularity (mobile). Desktop FilterBar shows this as a
                segmented control; mobile uses a Select so the touch-target
                rhythm matches the rest of the modal. Writes to the same
                `granularity` URL param — Auto/Hourly/Daily/Weekly. */}
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="filter-modal-granularity"
                className="text-sm font-medium"
              >
                Granularity
              </Label>
              <Select
                value={pendingFilters.granularity}
                onValueChange={(v) =>
                  setPending({ granularity: v as Granularity })
                }
              >
                <SelectTrigger
                  id="filter-modal-granularity"
                  className="min-h-11 w-full"
                  disabled={rowState('granularity').disabled}
                  aria-describedby={rowState('granularity').noteId}
                  data-testid="filter-modal-granularity"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRANULARITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rowState('granularity').note && (
                <p
                  id={rowState('granularity').noteId}
                  className="text-xs text-theme-text-secondary"
                  data-testid="filter-modal-granularity-inapplicable"
                >
                  {rowState('granularity').note}
                </p>
              )}
            </div>
          </div>
        </div>

        {/*
          Footer (post Bug F1): live-apply means no Apply button. "Done"
          dismisses the sheet; Reset clears the URL and closes. `shrink-0`
          pins it to the bottom of the flex column so body scroll can't push
          it off-screen. We keep `data-testid="filter-modal-apply"` on the
          Done button so any e2e selectors that tapped Apply to close the
          sheet still work — the action (close modal) is unchanged. Reset
          is now a ghost Button (not a bare <button>) so the tap affordance
          matches Done and Fitts' law is satisfied.
        */}
        {/*
          Overview P3 (2026-04-24 mobile audit): iPhone SE and notched devices
          clipped the Reset button under the home indicator / gesture bar.
          `env(safe-area-inset-bottom)` threads the OS-reported safe-area
          padding into the footer on supported devices; falls back to the
          tailwind `pb-3` rhythm via the max() guard.
        */}
        <div
          className="shrink-0 flex flex-col gap-2 border-t border-theme-border bg-theme-background px-4 pt-3"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <DialogClose asChild>
            <Button
              size="lg"
              className="min-h-11 w-full"
              data-testid="filter-modal-apply"
            >
              Done
            </Button>
          </DialogClose>
          <Button
            variant="ghost"
            size="lg"
            onClick={handleReset}
            className="min-h-11 w-full text-theme-text-secondary"
            data-testid="filter-modal-reset"
          >
            Reset filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
